export const CHART_RECOVERY_DELAY_MS = 300;
// Recoveries closer than this are merged into one
export const CHART_RECOVERY_MIN_INTERVAL_MS = 1000;
export const CHART_SLEEP_GAP_MS = 15 * 1000;
export const CHART_HEARTBEAT_INTERVAL_MS = 5 * 1000;

// ─── Chart types ────────────────────────────────────────────────────────────
// The numbers are saved in the chart settings (localStorage), so they stay
// as they were in the TradingView version (its SeriesType values).
export const CHART_TYPE_BARS = 0;
export const CHART_TYPE_CANDLES = 1;
export const CHART_TYPE_AREA = 3; // "Line" in our UI = Area with gradient
export const CHART_TYPE_HEIKIN_ASHI = 8;

/** Default chart type shown on first load (Candles). */
export const CHART_DEFAULT_TYPE = CHART_TYPE_CANDLES;


// ─── Candle colors ────────────────────────────────────────────────────────────
// These mirror $chart-up and $chart-down from _variables.sass.
// If the Sass variable changes, update these values too.
export const CANDLE_DEFAULT_UP_COLOR = "#2FA766";
export const CANDLE_DEFAULT_DOWN_COLOR = "#E2464A";

// ─── Shadow style: wick width of Candles and Heikin Ashi (E18) ───────────────
export const SHADOW_STYLE = {
  REGULAR: "regular",
  THICK: "thick",
};
export const SHADOW_STYLE_DEFAULT = SHADOW_STYLE.REGULAR;

/** Seconds in one minute, hour, day, week, month — used by resolutionToSeconds and spanToLabel. */
export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;
export const SECONDS_PER_WEEK = 604800;
export const SECONDS_PER_MONTH = 2592000;

/**
 * Converts a TV resolution string to its period in seconds.
 * For sub-minute (seconds) resolutions, delegates to getSecondsIntervalMs
 * from secondsHistory.js (returns null if not a seconds resolution).
 *
 * @param {string} resolution - TV resolution, e.g. "1", "60", "D", "15S"
 * @param {Function} getSecondsIntervalMsFn - imported getSecondsIntervalMs
 * @returns {number} seconds per bar
 */
export const resolutionToSeconds = (resolution, getSecondsIntervalMsFn) => {
  // Delegate seconds resolutions (e.g. "15S") to the canonical helper
  if (getSecondsIntervalMsFn) {
    const ms = getSecondsIntervalMsFn(resolution);

    if (ms !== null) return ms / 1000;
  }

  const r = String(resolution);

  if (r === "D" || r === "1D") return SECONDS_PER_DAY;
  if (r === "W" || r === "1W") return SECONDS_PER_WEEK;
  if (r === "M" || r === "1M") return SECONDS_PER_MONTH;

  const minutes = Number(r);

  if (!Number.isNaN(minutes) && minutes > 0) return minutes * 60;

  return 60; // fallback: 1 minute
};

// ─── Resolution label helpers ────────────────────────────────────────────────
/**
 * Converts a TV resolution string to the PO-style badge label.
 * Examples: "15S" → "S15", "1" → "M1", "60" → "H1", "D" → "D1".
 *
 * @param {string} resolution
 * @returns {string}
 */
export const resolutionToLabel = (resolution) => {
  const r = String(resolution);

  // Seconds: "15S" → "S15"
  if (/^\d+S$/.test(r)) return `S${r.slice(0, -1)}`;

  // Day / Week / Month
  if (r === "D" || r === "1D") return "D1";
  if (r === "W" || r === "1W") return "W1";
  if (r === "M" || r === "1M") return "MN";

  // Minutes: numeric strings
  const minutes = Number(r);

  if (!Number.isNaN(minutes)) {
    if (minutes < SECONDS_PER_HOUR / SECONDS_PER_MINUTE) return `M${minutes}`;
    if (minutes % 60 === 0) return `H${minutes / 60}`;
  }

  return r;
};

// ─── Visible span (zoom button, po-chart-visual-spec.md §6) ──────────────────
/**
 * Spans of the zoom menu (seconds: M10, M30, H1, H4, D1, D7, D14), ascending:
 * each shows that much time across the pane on the current timeframe; a span
 * the zoom limits cannot fit gets the nearest possible zoom.
 */
export const ZOOM_MENU_SPANS = [
  10 * SECONDS_PER_MINUTE,
  30 * SECONDS_PER_MINUTE,
  SECONDS_PER_HOUR,
  4 * SECONDS_PER_HOUR,
  SECONDS_PER_DAY,
  7 * SECONDS_PER_DAY,
  14 * SECONDS_PER_DAY,
];

/**
 * PO-style label of a time span, rounded to whole units:
 * 1260 → "M21", 28800 → "H8", 604800 → "D7"; under half a minute — seconds.
 *
 * @param {number} seconds
 * @returns {string|null} null for an unknown (zero) span
 */
export const spanToLabel = (seconds) => {
  if (!(seconds > 0)) return null;

  const minutes = Math.round(seconds / SECONDS_PER_MINUTE);

  if (minutes < 1) return `S${Math.round(seconds)}`;
  if (minutes < SECONDS_PER_HOUR / SECONDS_PER_MINUTE) return `M${minutes}`;

  const hours = Math.round(seconds / SECONDS_PER_HOUR);

  if (hours < SECONDS_PER_DAY / SECONDS_PER_HOUR) return `H${hours}`;

  return `D${Math.round(seconds / SECONDS_PER_DAY)}`;
};

// ─── Chart height ─────────────────────────────────────────────────────────────
// The resize handle under the time axis scales the height the page CSS gives the chart
export const CHART_HEIGHT_FACTOR_MIN = 0.75;
export const CHART_HEIGHT_FACTOR_MAX = 1.25;
export const CHART_HEIGHT_FACTOR_DEFAULT = 1;
